import { Portal } from '@radix-ui/react-portal';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { uid } from 'uid';

import Snackbar, {
  SnackbarButton,
  SnackbarStack,
  SnackbarType,
} from '@superfunnel/ui-core/Snackbar';

import {
  isLimitError,
  parseFirebaseError,
  parseLimitError,
} from '../utils/error-utils';

const CLOSE_AUTO = 5000;

const overlays = document.getElementById('overlays')!;

interface SnackbarItem {
  id: string;
  type: SnackbarType;
  message: string;
  description?: string;
  shownAt: Date;
  show: boolean;
  disableAutoClose: boolean;
  autoCloseAt: Date;
  autoClosePausedAt?: Date;
  buttons?: SnackbarButton[];
  onClose?: () => void;
}

interface SnackbarConfig {
  disableAutoClose?: boolean;
  id?: string; // Optional ID to allow custom IDs for snackbars
  description?: string;
  buttons?: SnackbarButton[];
  onClose?: () => void;
}

interface SnackbarContext {
  show: (
    message: string,
    type?: SnackbarType,
    config?: SnackbarConfig,
  ) => SnackbarItem;
  showError: (error: unknown, config?: SnackbarConfig) => SnackbarItem;
  showDebug: (
    message: string,
    type?: SnackbarType,
    config?: SnackbarConfig,
  ) => SnackbarItem | undefined;
  hide: (id?: string | string[]) => void;
  getSnackbarById: (id: string) => SnackbarItem | undefined;
}

export const SnackbarContext = createContext<SnackbarContext>({
  show: () => {
    throw new Error('show() not implemented');
  },
  showError: () => {
    throw new Error('showError() not implemented');
  },
  showDebug: () => {
    throw new Error('showDebug() not implemented');
  },
  hide: () => {},
  getSnackbarById: () => {
    throw new Error('getSnackbarById() not implemented');
  },
});

export function SnackbarContextProvider(props: any) {
  const [snackbars, setSnackbars] = useState<SnackbarItem[]>([]);

  const show = useCallback(
    (message: string, type: SnackbarType = 'info', config?: SnackbarConfig) => {
      const id = config?.id || uid(12);
      const shownAt = new Date();
      const snackbar = {
        id,
        type,
        message,
        description: config?.description,
        shownAt,
        show: true,
        disableAutoClose: config?.disableAutoClose || false,
        autoCloseAt: new Date(shownAt.getTime() + CLOSE_AUTO),
        buttons: config?.buttons,
        onClose: config?.onClose,
      };
      setSnackbars((prev) => [
        ...prev.filter((s) => s.id !== id), // Remove existing snackbar with the same ID
        snackbar,
      ]);

      return snackbar;
    },
    [],
  );

  const showDebug = useCallback(
    (message: string, type: SnackbarType = 'info') => {
      if (import.meta.env.NODE_ENV !== 'development') return;
      return show(message, type);
    },
    [show],
  );

  const showError = useCallback(
    (error: unknown, config?: SnackbarConfig) => {
      if (isLimitError(error)) {
        const details = parseLimitError(error);
        const message = details?.message || 'Limit exceeded';
        window.dispatchEvent(
          new CustomEvent('sf:limit-exceeded', { detail: details }),
        );
        // Return a placeholder — LimitExceededContext handles the actual UI
        return {
          id: 'limit-exceeded',
          type: 'warn' as SnackbarType,
          message,
          shownAt: new Date(),
          show: false,
          disableAutoClose: true,
          autoCloseAt: new Date(),
        };
      }
      const message = parseFirebaseError(error);
      return show(message, 'error', { disableAutoClose: true, ...config });
    },
    [show],
  );

  const hide = useCallback((id?: string | string[]) => {
    const isSnackbar = (sb: SnackbarItem) => {
      if (!id) return false;
      if (Array.isArray(id)) return id.includes(sb.id);
      return sb.id === id;
    };
    setSnackbars((prev) =>
      prev.some(isSnackbar)
        ? prev.map((snackbar) => {
            return isSnackbar(snackbar)
              ? { ...snackbar, show: false }
              : snackbar;
          })
        : prev,
    );
  }, []);

  const getSnackbarById = useCallback(
    (id: string) => {
      return snackbars.find((snackbar) => snackbar.id === id);
    },
    [snackbars],
  );

  const remove = useCallback((id: string) => {
    setSnackbars((prev) => prev.filter((snackbar) => snackbar.id !== id));
  }, []);

  const pauseAutoClose = useCallback((id: string) => {
    setSnackbars((prev) =>
      prev.map((snackbar) =>
        snackbar.id === id &&
        snackbar.show &&
        !snackbar.disableAutoClose &&
        !snackbar.autoClosePausedAt
          ? { ...snackbar, autoClosePausedAt: new Date() }
          : snackbar,
      ),
    );
  }, []);

  const resumeAutoClose = useCallback((id: string) => {
    const resumedAt = new Date();

    setSnackbars((prev) =>
      prev.map((snackbar) => {
        if (snackbar.id !== id || !snackbar.autoClosePausedAt) {
          return snackbar;
        }

        const pausedFor =
          resumedAt.getTime() - snackbar.autoClosePausedAt.getTime();

        return {
          ...snackbar,
          autoCloseAt: new Date(snackbar.autoCloseAt.getTime() + pausedFor),
          autoClosePausedAt: undefined,
        };
      }),
    );
  }, []);

  useEffect(() => {
    if (!snackbars.length) return;

    const autoCloseTimer = setInterval(() => {
      const now = new Date();
      const snackbarsToAutoClose = snackbars.filter(
        (snackbar) =>
          snackbar.show &&
          !snackbar.disableAutoClose &&
          !snackbar.autoClosePausedAt &&
          snackbar.autoCloseAt.getTime() < now.getTime(),
      );

      if (!snackbarsToAutoClose.length) return;

      setSnackbars((prev) =>
        prev.map((snackbar) => {
          if (snackbarsToAutoClose.some((s) => s.id === snackbar.id)) {
            return { ...snackbar, show: false };
          }

          return snackbar;
        }),
      );
    }, 100);

    return () => {
      clearInterval(autoCloseTimer);
    };
  }, [snackbars]);

  return (
    <SnackbarContext.Provider
      value={{
        show: show,
        showError: showError,
        showDebug: showDebug,
        hide: hide,
        getSnackbarById: getSnackbarById,
      }}
    >
      <div>{props.children}</div>
      <Portal container={overlays || undefined}>
        <div
          aria-live="assertive"
          className="pointer-events-none fixed inset-0 z-[9999] flex items-end pt-3 pr-[max(0.75rem,env(safe-area-inset-right))] pb-[max(0.75rem,env(safe-area-inset-bottom))] pl-[max(0.75rem,env(safe-area-inset-left))] lg:items-start lg:p-6"
          onPointerDownCapture={(e) => {
            const target = e.target as HTMLElement | null;
            if (target?.closest('[data-snackbar-card]')) {
              e.stopPropagation();
              e.preventDefault();
            }
          }}
        >
          <SnackbarStack>
            {snackbars.map((snackbar) => (
              <Snackbar
                show={snackbar.show}
                key={snackbar.id}
                type={snackbar.type}
                title={snackbar.message}
                description={snackbar.description}
                onPointerEnter={() => pauseAutoClose(snackbar.id)}
                onPointerLeave={() => resumeAutoClose(snackbar.id)}
                onClose={() => {
                  hide(snackbar.id);
                  snackbar.onClose?.();
                }}
                afterLeave={() => remove(snackbar.id)}
                buttons={snackbar.buttons}
              />
            ))}
          </SnackbarStack>
        </div>
      </Portal>
    </SnackbarContext.Provider>
  );
}

export const useSnackbarContext = () => {
  return useContext(SnackbarContext);
};
